/* eslint-disable @typescript-eslint/no-explicit-any */
import { ConfiguratorType } from "../../../../ConfiguratorType";
import { getHighestVisitedStep, getRailKmatCsticMap } from "../../../../Linear/redux/selectors";
import { sendRailUpdate } from "../../../api/sendRailConfiguratorUpdate";
import { sendRailMiscChange, sendRailStepChange } from "../../../api/sendRailStepChange";
import { sendRailDataReset } from "../../../api/sendRailStepReset";
import { sendRailResetConfigurator } from "../../../api/sendRailResetConfigurator";
import { sendRailAddToCart } from "../../../api/sendRailAddToCart";
import { RailSummaryData } from "../../../components/RailSummaryData";
import { RAIL_MIN_STEP_TO_BUY, RAIL_MIN_STEP_TO_SAVE } from "./railConfiguratorConstants";
import { type RootState } from "../../../../../redux/store";
import {
  BASE_DESIGNATION_SEPARATOR,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  isGuidewayPieceSizeCsticName,
} from "../../../../Linear/constants";
import { getCsticNumberVal, trimCollapseWhitespace } from "../../../../Linear/utils";
import { NumberFormatter as NF } from "../../../../../utils/NumberFormatter";
import { type ConfiguratorPageDefinition, type StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import { grabXmlTranslation } from "~/Configurators/grabExternalTranslation";
import { isNonNull } from "~/utils/reducers";
import { fetchSimpleSubstepValue } from "~/Configurators/Linear/components/fetchSimpleSubstepValue";
import { RAIL_CONFIGURATOR_PRODUCT_CODE } from "../../../../constants";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import * as RailCstic from "./RailsCstics";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/Configurators/Shaft/shaftGaHelper";

import { HelperCstics } from "~/Configurators/Rail/components/ConfigureRailSubstep/constants";
import type { LinearCstic } from "~/Configurators/Linear/models";
import { csticHasValue, getCsticsValue } from "~/Configurators/common/v2/helper";

const RAIL_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    resetLabelKey: "",
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);

      const railKmat = getRailKmatCsticMap(state);

      const designTypeCstic = railKmat["VMKB2_064"];
      const sizeCstic = railKmat["VBGR_064"];

      const selectionData = {
        series: designTypeCstic.value,
        size: sizeCstic.value,
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
    gaName: "",
  },
  {
    resetLabelKey: "",
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const substeps = state.linearConfigurator.substeps;

      const coating = grabXmlTranslation(summaryDataProvider, fetchSimpleSubstepValue(substeps.Coating) ?? "");
      const accuracy = grabXmlTranslation(summaryDataProvider, fetchSimpleSubstepValue(substeps.Accuracy) ?? "");

      const selectionData = {
        coating: trimCollapseWhitespace(coating),
        accuracy: trimCollapseWhitespace(accuracy),
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
    gaName: "",
  },
  {
    resetLabelKey: "",
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const substeps = state.linearConfigurator.substeps;

      const mounting_variant = grabXmlTranslation(
        summaryDataProvider,
        fetchSimpleSubstepValue(substeps.Mounting) ?? "",
      );
      const holes_closing_techniques = grabXmlTranslation(
        summaryDataProvider,
        fetchSimpleSubstepValue(substeps.Closure) ?? "",
      );

      const selectionData = {
        mounting_variant: trimCollapseWhitespace(mounting_variant),
        holes_closing_techniques: trimCollapseWhitespace(holes_closing_techniques),
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(3), selectionData);
    },
    gaName: "",
  },
  {
    resetLabelKey: "",
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);

      const railKmat = getRailKmatCsticMap(state);

      const locatingFaceCstic = railKmat[RailCstic.LocatingFace];
      const locatingFaceTile = locatingFaceCstic.values.find((val) => val.value === locatingFaceCstic.value);

      const set = {} as any;

      set.length = railKmat[RailCstic.GuidewayLength].value;

      const alValue = getCsticNumberVal(railKmat[RailCstic.AL]);
      set.aL =
        alValue !== null ? NF.getDefaultFormatter().format(Math.ceil((alValue + Number.EPSILON) * 100) / 100) : "";

      const arValue = getCsticNumberVal(railKmat[RailCstic.AR]);
      set.aR =
        arValue !== null ? NF.getDefaultFormatter().format(Math.ceil((arValue + Number.EPSILON) * 100) / 100) : "";

      if (railKmat[RailCstic.PartSizing].value !== null) {
        const label = railKmat[RailCstic.PartSizing].values.find(
          (v) => v.value === railKmat[RailCstic.PartSizing].value,
        )!.label;
        set.part_lengths = trimCollapseWhitespace(grabXmlTranslation(summaryDataProvider, label));
      }

      const partAmount = railKmat[RailCstic.PartAmount]?.value;
      set.part_amount = partAmount ? Number(partAmount) : "-";

      for (let j = 1; j <= 5; j++) {
        const cstic = railKmat[GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(j)];
        const csticNumericValue = getCsticNumberVal(cstic);
        set[`part_${j}_length`] = csticNumericValue !== null ? NF.getDefaultFormatter().format(csticNumericValue) : "-";
      }

      const locating_face = grabXmlTranslation(
        summaryDataProvider,
        locatingFaceTile?.label ?? locatingFaceCstic?.value ?? "-",
      );

      const selectionData = {
        locating_face: trimCollapseWhitespace(locating_face),
        guideway_configuration: set,
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(4), selectionData);
    },
    gaName: "",
  },
];

export const RAIL_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Rail,
  productCode: RAIL_CONFIGURATOR_PRODUCT_CODE,

  translationKeys: {
    pageTitle: "configurator.rail.name",
  },
  apiCalls: {
    update: sendRailUpdate,
    reset: sendRailResetConfigurator,
    stepChange: sendRailStepChange,
    stepReset: sendRailDataReset,
    miscChange: sendRailMiscChange,
    addToCart: sendRailAddToCart,
  },
  summary: RailSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);
    return step >= RAIL_MIN_STEP_TO_BUY && hasLengthSet;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= RAIL_MIN_STEP_TO_SAVE;
  },
  stepHardcodedData: RAIL_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    if ([RailCstic.GuidewayLength, RailCstic.AL, RailCstic.AR].includes(csticName)) {
      return true;
    }
    if (isGuidewayPieceSizeCsticName(csticName)) {
      return true;
    }
    return false;
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [series, sizeCode, version, guidewayDesign] = getCsticsValue(
      kmat,
      RailCstic.DesignType_Series,
      RailCstic.DesignType_SizeCode,
      RailCstic.DesignType_Version,
      RailCstic.DesignType_GuidewayDesign,
    );

    const join = (separator: string, ...values: string[]) => values.filter(isNonNull).join(separator);

    return join("-", join("", series, sizeCode), version, guidewayDesign) || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "rail",
  getGaStepName: (step) => RAIL_CONFIGURATOR_STEP_DATA[step]?.gaName,
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    const alCstic = cstics[RailCstic.AL];
    if (alCstic && alCstic.value) {
      alCstic.value = (+alCstic.value).toFixed(2);
    }

    const arCstic = cstics[RailCstic.AR];
    if (arCstic && arCstic.value) {
      arCstic.value = (+arCstic.value).toFixed(2);
    }

    const partSizingCstic = cstics[RailCstic.PartSizing];
    const manualSizingSelected = !!model.customSegmentLengthSelected;
    if (partSizingCstic) {
      partSizingCstic.label = "configurator.rail.cstic.partSizing.name";
      partSizingCstic.values.forEach((val) => {
        val.label = `configurator.rail.cstic.partSizing.${val.value}.name`;
      });

      // Remove "N" value"
      partSizingCstic.values = partSizingCstic.values.filter((cVal) => cVal.key !== "N");

      if (!manualSizingSelected) {
        // Logic defined in SCHAEFF-19247
        // If the sizing is not set manually - don't show whether it's A or J
        partSizingCstic.value = undefined;
      }
    }
    const partAmountCstic = cstics[RailCstic.PartAmount];
    if (partAmountCstic) {
      partAmountCstic.label = "configurator.rail.cstic.partAmount.name";
      partAmountCstic.values.forEach((val) => {
        val.label = `configurator.rail.cstic.partAmount.${val.value}.name`;
      });

      const pieceLimits = Object.values(model.guidewayPieceLimits)[0];
      if (manualSizingSelected) {
        const { min, max } = pieceLimits;
        partAmountCstic.values = partAmountCstic.values.slice(Math.max(min - 1, 0), max);
      }
    }

    const locatingFaceCstic = cstics[RailCstic.LocatingFace];
    if (locatingFaceCstic) {
      locatingFaceCstic.label = "configurator.rail.cstic.locatingFace.name";
      locatingFaceCstic.values?.forEach((val) => {
        val.label = `configurator.rail.cstic.locatingFace.${val.value}.name`;
      });
    }

    const mountingSubstep = model.substeps[SubstepType.Mounting];
    if (mountingSubstep) {
      const DESIGN_VALUES_FOR_DISABLED_MOUNTING_UNDER = ["TKSD"];
      const SIZE_VALUES_FOR_DISABLED_MOUNTING_UNDER = ["30", "35", "45"];

      const designTypeValue = kmat.cstics[RailCstic.Series]?.value ?? "";
      const sizeValue = kmat.cstics[RailCstic.Size]?.value ?? "";

      const clearCsticsToEnable =
        !DESIGN_VALUES_FOR_DISABLED_MOUNTING_UNDER.includes(designTypeValue) ||
        !SIZE_VALUES_FOR_DISABLED_MOUNTING_UNDER.includes(sizeValue);

      const underTile = mountingSubstep.tiles?.find((tile) => tile.cstics[RailCstic.MountingHoleType] === "U");
      if (underTile && clearCsticsToEnable) {
        underTile.csticsToEnable = {};
      }
    }

    if (model.minLength) {
      cstics[HelperCstics.totalLengthMin] = {
        ...EMPTY_FAKE_CSTIC,
        key: HelperCstics.totalLengthMin,
        name: HelperCstics.totalLengthMin,
        value: model.minLength.toString(),
      };
    }
    if (model.maxLength) {
      cstics[HelperCstics.totalLengthMax] = {
        ...EMPTY_FAKE_CSTIC,
        key: HelperCstics.totalLengthMax,
        name: HelperCstics.totalLengthMax,
        value: model.maxLength.toString(),
      };
    }

    return model;
  },
} as const;

const EMPTY_FAKE_CSTIC: LinearCstic = {
  key: "",
  name: "",
  label: "",
  value: "",
  groupId: "",
  values: [],
  type: "",
  info: "",
  description: "",
  readonly: false,
  fake: true,
};

const sendStepCompletedEvent = makeSendStepCompletedEvent(RAIL_DEFINITION);
